/**
 * Where the Mailbox section's data comes from, three ways:
 *  - `http`: ows-mail, called directly on its own host. A configured host ALWAYS wins.
 *  - `mock`: the local seed and simulated round trips, ONLY when a developer chose them on purpose
 *    (`window.config.mailboxFixtures === true`: `VITE_MAILBOX_FIXTURES=true` in `.env.local`, through
 *    `src/dev.js`; the Playwright CT harness sets it too). The configmap never emits that key, so no
 *    deployed environment reaches the fixtures. Says so once in the console when it is in force.
 *  - `unavailable`: neither. The section says the mail service is not available here, shows no
 *    fixtures and makes no call. A missing line of configuration is not a reason to show invented
 *    mailboxes (and invented money warnings) as if they were the brand's.
 *
 * The deploy config decides: an environment whose `window.config` carries a non-empty
 * `owsMailApiUrl` calls the service; one without it shows the section as unavailable and makes no
 * call. Nothing is rebuilt to change environments. The configmap emits the key from `ows_mail_api_url` in the Helm
 * values, or, when that is empty, on spark-test environments only (the shared test ows-mail); prod
 * gets no key unless it sets its own host. In dev it comes from `VITE_OWS_MAIL_API_URL`
 * in `.env.local`, through `src/dev.js`. This reader has no default host of its own and no relative
 * fallback: an absent key is `unavailable` (or `mock` when chosen), never a guess. The switch is
 * decided once, at module load (`window.config` is static after boot); the base URL itself is read
 * on every call.
 *
 * SWITCHING AN ENVIRONMENT FROM MOCK TO LIVE: both sources share one per-brand browser record (the
 * live list merges its billing from it), so a browser that used the fixtures carries their billing
 * state onto real mailboxes with the same address. Clear the site's local storage in such a browser
 * after the switch.
 *
 * The one place that reads the environment for the mailbox, so nothing else does. It lives here, not
 * in the section's folder, because Home reads it too and Home imports nothing from that folder; the
 * section reaches it through `pages/mailbox/api/source.ts`.
 */
export type MailboxSource = 'mock' | 'http' | 'unavailable';

/** ows-mail's base URL (`https://<host>/api/ows-mail/v1`, no trailing slash) from the deploy config,
 *  or `null` when this environment has none. Read from `window.config` on every call. */
export const owsMailApiUrl = (): string | null => {
  const value = typeof window === 'undefined' ? undefined : window.config?.owsMailApiUrl;
  if (typeof value !== 'string') return null;
  const base = value.trim().replace(/\/+$/, '');
  return base === '' ? null : base;
};

/** Whether a developer chose the fixtures on purpose. Only the literal `true` counts. */
const fixturesChosen = (): boolean => typeof window !== 'undefined' && window.config?.mailboxFixtures === true;

const resolveSource = (): MailboxSource => {
  if (owsMailApiUrl() !== null) return 'http';
  if (!fixturesChosen()) return 'unavailable';
  // eslint-disable-next-line no-console -- once, at load: nobody may mistake the fixtures for real data
  console.info('[mailbox] Running on fixtures (mailboxFixtures): these mailboxes are not real.');
  return 'mock';
};

export const MAILBOX_SOURCE: MailboxSource = resolveSource();
